import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, Mail, Trash2, Shield, Edit, Eye, X, Link, UserPlus, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamManagement = () => {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteMethod, setInviteMethod] = useState('email'); // 'email', 'link', 'direct'
  const [currentUserRole, setCurrentUserRole] = useState('project_manager');
  const [generatedLink, setGeneratedLink] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    company_role: 'editor'
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchTeamData();
  }, []);

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/auth/me`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (response.data.user) {
        setCurrentUserRole(response.data.user.role);
      }
    } catch (error) {
      console.error('Failed to fetch current user');
    }
  };

  const fetchTeamData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [membersRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/team/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/team/invitations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setMembers(membersRes.data.members || []);
      setInvitations(invitationsRes.data.invitations || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only admins can manage team members');
      } else {
        toast.error('Failed to load team data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (inviteMethod === 'email' && !inviteForm.email) {
      toast.error('Email is required');
      return;
    }

    if (inviteMethod === 'direct' && !inviteForm.email) {
      toast.error('Email is required');
      return;
    }

    const token = localStorage.getItem('token');
    setProcessing(true);

    try {
      if (inviteMethod === 'email') {
        // Email invitation (existing)
        const inviteData = {
          ...inviteForm,
          user_role: currentUserRole
        };
        
        await axios.post(`${API}/team/invite`, inviteData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Invitation sent successfully!');
        setShowInviteModal(false);
        setInviteForm({ email: '', company_role: 'editor' });
        fetchTeamData();

      } else if (inviteMethod === 'link') {
        // Generate generic link
        const response = await axios.post(
          `${API}/team/invite-link`,
          null,
          {
            params: { company_role: inviteForm.company_role },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setGeneratedLink(response.data.link);
        setShowInviteModal(false);
        setShowLinkModal(true);
        toast.success('Invite link generated!');

      } else if (inviteMethod === 'direct') {
        // Create directly
        const response = await axios.post(
          `${API}/team/create-direct`,
          null,
          {
            params: { 
              email: inviteForm.email,
              company_role: inviteForm.company_role
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setCreatedCredentials({
          email: response.data.email,
          password: response.data.temporary_password
        });
        setShowInviteModal(false);
        setShowCredentialsModal(true);
        toast.success('Team member created!');
        fetchTeamData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/team/members/${userId}/role`, 
        { company_role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Role updated successfully');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName}?`)) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/team/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Member removed successfully');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/team/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Invitation cancelled');
      fetchTeamData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4 text-red-500" />;
      case 'editor': return <Edit className="w-4 h-4 text-blue-500" />;
      case 'viewer': return <Eye className="w-4 h-4 text-gray-500" />;
      default: return null;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'editor': return 'bg-blue-100 text-blue-700';
      case 'viewer': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Team Members Card */}
      <Card className="bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle style={{ color: '#1B3240' }} className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members ({members.length})
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">Manage your company's team members and roles</p>
            </div>
            <Button 
              onClick={() => setShowInviteModal(true)} 
              className="btn-snow"
            >
              <Mail className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No team members yet</p>
          ) : (
            <div className="space-y-3">
              {members.map(member => (
                <div 
                  key={member.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {member.first_name} {member.last_name}
                          {!member.first_name && !member.last_name && member.email}
                        </p>
                        <p className="text-sm text-gray-600">{member.email}</p>
                        {member.job_title && (
                          <p className="text-xs text-gray-500 mt-1">{member.job_title}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Role Badge */}
                    <div className="flex items-center gap-2">
                      {getRoleIcon(member.company_role)}
                      <select
                        value={member.company_role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(member.company_role)}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </div>

                    {/* User Role Badge */}
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      {member.role === 'resource_manager' ? 'RM' : 'PM'}
                    </span>

                    {/* Remove Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id, `${member.first_name} ${member.last_name}` || member.email)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations Card */}
      {invitations.length > 0 && (
        <Card className="bg-white">
          <CardHeader>
            <CardTitle style={{ color: '#1B3240' }}>
              Pending Invitations ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map(inv => (
                <div 
                  key={inv.id} 
                  className="flex items-center justify-between p-4 border border-yellow-200 bg-yellow-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{inv.invited_email}</p>
                    <p className="text-sm text-gray-600">
                      Invited by {inv.invited_by.name || inv.invited_by.email}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Role: <span className="font-medium">{inv.company_role}</span> • 
                      Expires: {new Date(inv.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancelInvitation(inv.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Modal with 3 Methods */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ color: '#1B3240' }}>
                Invite Team Member
              </h2>
              <button 
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteMethod('email');
                  setInviteForm({ email: '', company_role: 'editor' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Method Selector */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setInviteMethod('email')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  inviteMethod === 'email' 
                    ? 'bg-green-100 text-green-700 border-2 border-green-500' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Mail className="w-4 h-4 mx-auto mb-1" />
                By Email
              </button>
              <button
                type="button"
                onClick={() => setInviteMethod('link')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  inviteMethod === 'link' 
                    ? 'bg-green-100 text-green-700 border-2 border-green-500' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Link className="w-4 h-4 mx-auto mb-1" />
                Gen. Link
              </button>
              <button
                type="button"
                onClick={() => setInviteMethod('direct')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  inviteMethod === 'direct' 
                    ? 'bg-green-100 text-green-700 border-2 border-green-500' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <UserPlus className="w-4 h-4 mx-auto mb-1" />
                Create Direct
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              {/* Email field (for email and direct methods) */}
              {(inviteMethod === 'email' || inviteMethod === 'direct') && (
                <div>
                  <Label htmlFor="invite_email">Email Address</Label>
                  <Input
                    id="invite_email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="colleague@example.com"
                    required
                  />
                </div>
              )}

              {/* Role selector */}
              <div>
                <Label htmlFor="company_role">Company Role</Label>
                <select
                  id="company_role"
                  value={inviteForm.company_role}
                  onChange={(e) => setInviteForm({ ...inviteForm, company_role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="admin">Admin - Full access</option>
                  <option value="editor">Editor - Can manage projects & resources</option>
                  <option value="viewer">Viewer - Read-only access</option>
                </select>
              </div>

              {/* Method-specific info */}
              {inviteMethod === 'email' && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>📧 Email Invitation</strong>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    An email will be sent with an invitation link valid for 7 days
                  </p>
                </div>
              )}

              {inviteMethod === 'link' && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                  <p className="text-sm text-purple-800">
                    <strong>🔗 Generic Invite Link</strong>
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    Generate a shareable link valid for 30 days. Anyone with the link can join.
                  </p>
                </div>
              )}

              {inviteMethod === 'direct' && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                  <p className="text-sm text-orange-800">
                    <strong>⚡ Direct Creation</strong>
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    Account created immediately with temporary password sent by email
                  </p>
                </div>
              )}

              {/* User type info */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-sm text-gray-800">
                  <strong>User Type:</strong> {currentUserRole === 'project_manager' ? 'Project Manager (PM)' : 'Resource Manager (RM)'}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Invited members will have the same type as you
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteMethod('email');
                    setInviteForm({ email: '', company_role: 'editor' });
                  }}
                  className="flex-1"
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 btn-snow" disabled={processing}>
                  {processing ? 'Processing...' : (
                    inviteMethod === 'email' ? 'Send Invitation' :
                    inviteMethod === 'link' ? 'Generate Link' :
                    'Create Account'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generated Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-green-700">
                🔗 Invite Link Generated!
              </h2>
              <button 
                onClick={() => setShowLinkModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Share this link with anyone you want to invite. It's valid for 30 days.
              </p>

              <div className="bg-gray-50 border rounded-md p-3">
                <p className="text-xs text-gray-500 mb-1">Invitation Link</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm text-gray-800 flex-1 overflow-x-auto">
                    {generatedLink}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedLink);
                      toast.success('Link copied to clipboard!');
                    }}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Copy link"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <Button 
                onClick={() => setShowLinkModal(false)} 
                className="w-full btn-snow"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Created Credentials Modal */}
      {showCredentialsModal && createdCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-orange-700">
                ⚡ Account Created!
              </h2>
              <button 
                onClick={() => setShowCredentialsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                <p className="text-sm text-orange-800 font-medium">
                  ⚠️ Important: Save these credentials
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  The temporary password won't be shown again. It has also been sent by email.
                </p>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 border rounded-md p-3">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-gray-800 flex-1">
                      {createdCredentials.email}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdCredentials.email);
                        toast.success('Email copied!');
                      }}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-md p-3">
                  <p className="text-xs text-gray-500 mb-1">Temporary Password</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-gray-800 flex-1 font-mono">
                      {createdCredentials.password}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdCredentials.password);
                        toast.success('Password copied!');
                      }}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setShowCredentialsModal(false)} 
                className="w-full btn-snow"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Role Descriptions Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 mb-3">Role Permissions</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 text-red-500" />
              <div>
                <strong>Admin:</strong> Full access - Can manage team, projects, resources, and company settings
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Edit className="w-4 h-4 mt-0.5 text-blue-500" />
              <div>
                <strong>Editor:</strong> Can create and edit projects/resources, send messages, but cannot manage team
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Eye className="w-4 h-4 mt-0.5 text-gray-500" />
              <div>
                <strong>Viewer:</strong> Read-only access - Can view all data but cannot make any changes
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamManagement;
